import { Box, Grid, MenuItem, Select, Tab, Tabs, Typography } from "@mui/material"
import { FC, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { useAppDispatch, useAppSelector } from "../../../../core/app/hooks"
import { fetchClinicalSettings } from "../../../../core/app/slices/user/userApis"
import { CENETER_COLUMN_ALIGN } from "../../../../theme/theme.util"
import { UButton, UText, UModalPopup } from "../../../../ui-component"
import DirectPrintAligner from "./DirectPrintAligner"
import LuxCreo from "./LuxCreo"
import StandardModel from "./StandardModel"
import { PrintModelType } from "@/core/app/slices/user/user.type"
import { RootState } from "@/core/app/store"
import { useClinicalSettingsUpdate } from "@/hooks/useClinicalSettingsUpdate"
import { useCallbackPrompt } from "@/hooks/useCallbackPrompt"
import WarningAmberIcon from '@mui/icons-material/WarningAmber'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2, pb: 2 }}>{children}</Box>}
    </div>
  )
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  }
}
const STLPrinting: FC = () => {
  const {
    clinicalSettings: { PrintDefaultModel },
  } = useAppSelector((state: RootState) => state.userService)
  const { t } = useTranslation("common")
  const [value, setValue] = useState(0)
  const [defaultModel, setDefaultModel] = useState<PrintModelType>(
    PrintModelType.Standard,
  )
  const { isRouteChangeBlocked } = useAppSelector(
    (state: RootState) => state.navigationPopup,
  )
  const [showPrompt, confirmNavigation, cancelNavigation] =
  useCallbackPrompt(isRouteChangeBlocked)
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue)
  }
  const onPrintSettingChange = useClinicalSettingsUpdate()
  const dispatch = useAppDispatch()
  const handleCancelNavigation = () => {
    if (typeof cancelNavigation === 'function') {
      cancelNavigation()
    }
  }
  useEffect(() => {
    dispatch(fetchClinicalSettings())
  }, [dispatch])
  useEffect(() => {
    if (PrintDefaultModel) {
      setDefaultModel(PrintDefaultModel)
    }
  }, [PrintDefaultModel])
  return (
    <Box sx={{ width: "1089px", p: 4 }}>
      <Box
        sx={{ ...CENETER_COLUMN_ALIGN, justifyContent: "center", pb: "16px" }}
      >
        <UText variant={"h4"} style={{ padding: "4px 0" }}>
          {t("stlPrinting.title")}
        </UText>
        <UText variant="body1" sxProp={{ color: "text.secondary" }}>
          {t("stlPrinting.desc")}
        </UText>
      </Box>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          pb: "16px",
        }}
      >
        <UText variant={"h6"}>{t("stlPrinting.SelectDefaultOutput")}</UText>
        <Select
          sx={{ width: "260px", height: "40px", marginLeft: "8px" }}
          displayEmpty
          value={defaultModel}
          onChange={(eve) => {
            setDefaultModel(eve.target.value as PrintModelType)
            onPrintSettingChange({
              PrintDefaultModel: eve.target.value as PrintModelType,
            })
          }}
        >
          <MenuItem
            value={PrintModelType.Standard}
          >{`Standard Model`}</MenuItem>
          <MenuItem
            value={PrintModelType.DPA}
          >{`DPA (Direct Print Aligner)`}</MenuItem>
          <MenuItem value={PrintModelType.Luxcreo}>{`LuxCreo`}</MenuItem>
        </Select>
      </Box>
      <Box>
        <Grid>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label="tabs for STL Printing"
            >
              <Tab label="Standard Model" {...a11yProps(0)} />
              <Tab label="DPA (Direct Print Aligner)" {...a11yProps(1)} />
              <Tab label="LuxCreo" {...a11yProps(2)} />
            </Tabs>
          </Box>
          <CustomTabPanel value={value} index={0}>
            <StandardModel />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={1}>
            <DirectPrintAligner />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={2}>
            <LuxCreo />
          </CustomTabPanel>
        </Grid>
      </Box>
            
      {/* Navigation blocking dialog */}
      <UModalPopup
        isAlert={!!showPrompt}
        title={
          <Box sx={{display: "flex", gap: 1}}>
            <WarningAmberIcon sx={{ color: "#D32F2F" }} />
            <UText variant="h6">{t(`stlPrinting.LabelComposer`)}</UText>
          </Box>
        }
        content={
          <UText variant="body1">
            {t(`stlPrinting.blockpopupContent`)}
          </UText>
        }
        sxModalProps={{
          "#titleCntr": {
            padding: "16px 24px",
          },
          "#contentCntr": {
            padding: "20px 24px",
          },
          "#btnListCntr": {
            padding: 1,
            gap: 1,
          },
        }}
        btnList={[
          <UButton
            key="resolve"
            variant="contained"
            btnText="Resolve"
            onClickHandler={handleCancelNavigation}
            sxProp={{
              minWidth: 80,
              height: 36,
            }}
          />
        ]}
      />
    </Box>
  )
}

export default STLPrinting
